import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Palette, Users, MessageSquareQuote, GraduationCap, FileText, CalendarDays,
  Trophy, Inbox, Plus, Trash2, Save, Check, RefreshCw, ExternalLink, Sparkles, Images, Activity, Star, KeyRound, Gauge,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite, SCHEMES } from "@/context/ThemeContext";
import { Logo } from "@/components/Logo";

const TABS = [
  { id: "reports", label: "Reports", Icon: Gauge },
  { id: "appearance", label: "Appearance", Icon: Palette },
  { id: "leads", label: "Leads", Icon: Inbox },
  { id: "offers", label: "Offers", Icon: Sparkles },
  { id: "reviews", label: "Reviews", Icon: Star },
  { id: "content", label: "Content", Icon: FileText },
  { id: "courses", label: "Courses", Icon: GraduationCap },
  { id: "testimonials", label: "Testimonials", Icon: MessageSquareQuote },
  { id: "team", label: "Team", Icon: Users },
  { id: "gallery", label: "Gallery", Icon: Images },
  { id: "activities", label: "Activities", Icon: Activity },
  { id: "events", label: "Events", Icon: CalendarDays },
  { id: "achievements", label: "Achievements", Icon: Trophy },
  { id: "account", label: "Account", Icon: KeyRound },
];

const inputCls = "w-full px-3.5 py-2.5 rounded-lg bg-surface border border-c text-t outline-none focus:border-primary-c text-sm";
const labelCls = "text-xs font-semibold text-muted-c mb-1.5 block uppercase tracking-wide";

function GoogleReviewSync() {
  const [loading, setLoading] = useState(false);
  const sync = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/reviews/sync-google");
      if (data.configured) toast.success(`Synced ${data.count} five-star Google review(s).`);
      else toast.message("Google sync not configured yet", { description: data.message });
    } catch {
      toast.error("Google sync failed. Please try again.");
    } finally { setLoading(false); }
  };
  return (
    <div className="glass rounded-2xl p-5 mb-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="google-sync-panel">
      <div>
        <div className="font-display font-bold text-t flex items-center gap-2"><Star size={16} className="text-primary-c" /> Live Google Reviews</div>
        <p className="text-muted-c text-sm mt-1">Auto-pulls 5-star Google reviews for both centres. Only 5★ reviews are shown on the website. Add Just Dial / Facebook / Instagram / SpeakEase reviews manually below.</p>
      </div>
      <button onClick={sync} disabled={loading} className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2 shrink-0 disabled:opacity-60" data-testid="google-sync-btn">
        <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> {loading ? "Syncing…" : "Sync Google Now"}
      </button>
    </div>
  );
}


export default function AdminDashboard() {
  const { logout } = useAuth();
  const { refresh } = useSite();
  const navigate = useNavigate();
  const [tab, setTab] = useState("appearance");

  return (
    <div className="min-h-screen bg-bg flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-c p-5 lg:min-h-screen">
        <div className="mb-8"><Logo /></div>
        <nav className="flex lg:flex-col gap-1 overflow-x-auto hide-scrollbar">
          {TABS.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setTab(id)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                tab === id ? "bg-primary-c text-on-primary" : "text-t hover:bg-surface"
              }`} data-testid={`admin-tab-${id}`}>
              <Icon size={17} /> {label}
            </button>
          ))}
        </nav>
        <div className="mt-6 flex lg:flex-col gap-2">
          <button onClick={() => window.open("/", "_blank")} className="btn-ghost px-4 py-2.5 text-sm inline-flex items-center gap-2"><ExternalLink size={15} /> View Site</button>
          <button onClick={() => { logout(); navigate("/"); }} className="btn-ghost px-4 py-2.5 text-sm inline-flex items-center gap-2" data-testid="admin-logout"><LogOut size={15} /> Logout</button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-5 sm:p-8 max-w-5xl">
        {tab === "reports" && <Reports />}
        {tab === "account" && <Account />}
        {tab === "appearance" && <Appearance refresh={refresh} />}
        {tab === "leads" && <Leads />}
        {tab === "reviews" && <><GoogleReviewSync /><CrudPanel title="Reviews & Ratings" endpoint="reviews" refresh={refresh}
          fields={[{ key: "name", label: "Reviewer Name" }, { key: "rating", label: "Rating (1-5)" }, { key: "platform", label: "Platform (Google / Just Dial / Facebook / Instagram / SpeakEase)" }, { key: "imageUrl", label: "Reviewer Photo (optional)", type: "image" }, { key: "text", label: "Review", type: "textarea" }]} /></>}
        {tab === "offers" && <CrudPanel title="Festival Offers" endpoint="offers" refresh={refresh}
          fields={[{ key: "title", label: "Offer Title" }, { key: "subtitle", label: "Subtitle" }, { key: "imageUrl", label: "Offer Image", type: "image" }, { key: "cta", label: "Button Text" }, { key: "startDate", label: "Start Date", type: "date" }, { key: "endDate", label: "End Date", type: "date" }, { key: "active", label: "Active (show on site)", type: "checkbox" }]} />}
        {tab === "content" && <ContentEditor refresh={refresh} />}
        {tab === "courses" && <CoursesPanel refresh={refresh} />}
        {tab === "testimonials" && <CrudPanel title="Testimonials" endpoint="testimonials" refresh={refresh}
          fields={[{ key: "name", label: "Name" }, { key: "course", label: "Course" }, { key: "year", label: "Batch / Year" }, { key: "profession", label: "Profession (optional)" }, { key: "imageUrl", label: "Learner Photo", type: "image" }, { key: "quote", label: "Quote", type: "textarea" }]} />}
        {tab === "gallery" && <CrudPanel title="Photo Gallery" endpoint="gallery" refresh={refresh}
          fields={[{ key: "imageUrl", label: "Photo", type: "image" }, { key: "caption", label: "Caption" }, { key: "category", label: "Category (e.g. Campus, Events, Batches)" }]} />}
        {tab === "activities" && <CrudPanel title="Student Activities" endpoint="activities" refresh={refresh}
          fields={[{ key: "title", label: "Activity Title" }, { key: "category", label: "Category (Robotics, Drone, 3D Printing...)" }, { key: "icon", label: "Icon name (lucide)" }, { key: "imageUrl", label: "Photo (optional)", type: "image" }, { key: "description", label: "Description", type: "textarea" }]} />}
        {tab === "team" && <CrudPanel title="Team Members" endpoint="team" refresh={refresh}
          fields={[{ key: "name", label: "Name" }, { key: "designation", label: "Designation" }, { key: "imageUrl", label: "Photo URL", type: "image" }]} />}
        {tab === "events" && <CrudPanel title="Events" endpoint="events" refresh={refresh}
          fields={[{ key: "title", label: "Title" }, { key: "date", label: "Date", type: "date" }, { key: "description", label: "Description", type: "textarea" }, { key: "archived", label: "Archived (past event)", type: "checkbox" }]} />}
        {tab === "achievements" && <CrudPanel title="Achievements" endpoint="achievements" refresh={refresh}
          fields={[{ key: "title", label: "Title" }, { key: "category", label: "Type", type: "select", options: ["Institute", "Learner"] }, { key: "description", label: "Description", type: "textarea" }]} />}
      </main>
    </div>
  );
}

function Header({ title, subtitle }) {
  return (
    <div className="mb-7">
      <h1 className="font-display font-bold text-3xl text-t">{title}</h1>
      {subtitle && <p className="text-muted-c mt-1.5">{subtitle}</p>}
    </div>
  );
}

/* ---------------- Appearance ---------------- */
function Appearance({ refresh }) {
  const { scheme, setScheme, data } = useSite();
  const [autoRotate, setAutoRotate] = useState(data?.settings?.auto_rotate ?? true);
  const [saving, setSaving] = useState(false);

  const save = async (schemeId, rotate) => {
    setSaving(true);
    try {
      await api.put("/settings", { color_scheme: schemeId, auto_rotate: rotate });
      await refresh();
      toast.success("Appearance saved — applied site-wide.");
    } catch { toast.error("Failed to save."); }
    setSaving(false);
  };

  return (
    <div data-testid="appearance-panel">
      <Header title="Appearance" subtitle="Pick a palette. Preview instantly, then save to apply site-wide." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {SCHEMES.map((s) => (
          <button key={s.id} onClick={() => { setScheme(s.id); }}
            className={`text-left rounded-2xl p-5 border-2 transition-all ${scheme === s.id ? "border-primary-c" : "border-c"} ${s.id}`}
            style={{ background: "var(--surface)" }} data-testid={`scheme-${s.id}`}>
            <div className="flex gap-2 mb-4">
              <span className="w-8 h-8 rounded-full" style={{ background: "var(--primary)" }} />
              <span className="w-8 h-8 rounded-full" style={{ background: "var(--secondary)" }} />
              <span className="w-8 h-8 rounded-full border border-c" style={{ background: "var(--bg)" }} />
            </div>
            <div className="font-display font-bold text-sm" style={{ color: "var(--text)" }}>{s.name}</div>
            {scheme === s.id && <span className="inline-flex items-center gap-1 text-xs mt-2 font-semibold" style={{ color: "var(--primary)" }}><Check size={13} /> Active preview</span>}
          </button>
        ))}
      </div>

      <div className="glass rounded-2xl p-6 mt-6 flex items-center justify-between gap-4">
        <div>
          <div className="font-display font-bold text-t flex items-center gap-2"><RefreshCw size={16} className="text-primary-c" /> Auto-rotate monthly</div>
          <p className="text-sm text-muted-c mt-1">Automatically switch to the next palette on the 1st of every month (unless changed manually).</p>
        </div>
        <button onClick={() => setAutoRotate((v) => !v)}
          className={`w-14 h-8 rounded-full p-1 transition-colors shrink-0 ${autoRotate ? "bg-primary-c" : "bg-surface border border-c"}`} data-testid="auto-rotate-toggle">
          <span className={`block w-6 h-6 rounded-full bg-[var(--bg)] transition-transform ${autoRotate ? "translate-x-6" : ""}`} />
        </button>
      </div>

      <button onClick={() => save(scheme, autoRotate)} disabled={saving} className="btn-primary px-7 py-3.5 mt-6 inline-flex items-center gap-2" data-testid="save-appearance">
        <Save size={17} /> {saving ? "Saving…" : "Save Appearance"}
      </button>
    </div>
  );
}

/* ---------------- Leads ---------------- */
const STATUSES = ["New", "Contacted", "Enrolled", "Not Interested"];
function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/leads"); setLeads(data); } catch { toast.error("Failed to load leads"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    setLeads((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
    toast.success("Status updated");
  };
  const remove = async (id) => {
    await api.delete(`/leads/${id}`);
    setLeads((l) => l.filter((x) => x.id !== id));
  };

  return (
    <div data-testid="leads-panel">
      <Header title="Admission Leads" subtitle={`${leads.length} enquiries received.`} />
      {loading ? <p className="text-muted-c">Loading…</p> : leads.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-muted-c">No leads yet.</div>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => (
            <div key={l.id} className="glass rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`lead-${l.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-t">{l.fullName} · <a href={`tel:${l.mobile}`} className="text-primary-c">{l.mobile}</a></div>
                <div className="text-sm text-muted-c mt-1">{l.courseInterest} · {l.batchTime} · via {l.source}{l.email ? ` · ${l.email}` : ""}</div>
                <div className="text-xs text-muted-c mt-1">{new Date(l.created_at).toLocaleString("en-IN")}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} className={`${inputCls} w-auto`} data-testid={`lead-status-${l.id}`}>
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
                <button onClick={() => remove(l.id)} className="p-2.5 rounded-lg btn-ghost text-red-400"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Reports ---------------- */
function Reports() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  const cards = [
    { k: "leads", label: "Total Leads" }, { k: "leads_new", label: "New Leads" }, { k: "courses", label: "Courses" },
    { k: "testimonials", label: "Testimonials" }, { k: "reviews", label: "Reviews" }, { k: "offers", label: "Offers" },
    { k: "gallery", label: "Gallery Photos" }, { k: "activities", label: "Activities" }, { k: "achievements", label: "Achievements" },
    { k: "events", label: "Events" }, { k: "team", label: "Team" },
  ];
  return (
    <div data-testid="reports-panel">
      <Header title="Reports" subtitle="A quick overview of your content and leads." />
      {!stats ? <p className="text-muted-c">Loading…</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.k} className="glass rounded-2xl p-5" data-testid={`stat-${c.k}`}>
              <div className="font-display font-black text-4xl text-primary-c">{stats[c.k] ?? 0}</div>
              <div className="text-sm text-muted-c mt-1">{c.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Account ---------------- */
function Account() {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [cf, setCf] = useState("");
  const [email, setEmail] = useState(""); const [phone, setPhone] = useState("");
  const changePw = async (e) => {
    e.preventDefault();
    if (nw !== cf) { toast.error("New passwords do not match."); return; }
    try { await api.post("/auth/change-password", { current_password: cur, new_password: nw }); toast.success("Password changed."); setCur(""); setNw(""); setCf(""); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const saveCreds = async (e) => {
    e.preventDefault();
    const body = {}; if (email) body.email = email; if (phone) body.phone = phone;
    if (!email && !phone) { toast.error("Enter a new email or mobile."); return; }
    try { await api.put("/auth/credentials", body); toast.success("Login identifiers updated. Use them next login."); setEmail(""); setPhone(""); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="account-panel">
      <Header title="Account" subtitle="Change your password and login identifiers." />
      <div className="grid gap-6 lg:grid-cols-2 max-w-3xl">
        <form onSubmit={changePw} className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-display font-bold text-t">Change Password</h3>
          <input type="password" className={inputCls} placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="acc-current-pw" />
          <input type="password" className={inputCls} placeholder="New password (min 6)" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="acc-new-pw" />
          <input type="password" className={inputCls} placeholder="Confirm new password" value={cf} onChange={(e) => setCf(e.target.value)} data-testid="acc-confirm-pw" />
          <button className="btn-primary px-6 py-2.5" data-testid="acc-save-pw">Update Password</button>
        </form>
        <form onSubmit={saveCreds} className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-display font-bold text-t">Login Identifiers</h3>
          <p className="text-xs text-muted-c">Leave blank to keep current. You can log in with either email or mobile.</p>
          <input className={inputCls} placeholder="New login email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="acc-email" />
          <input className={inputCls} placeholder="New login mobile" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="acc-phone" />
          <button className="btn-primary px-6 py-2.5" data-testid="acc-save-creds">Update Identifiers</button>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Content editor ---------------- */
function ContentEditor({ refresh }) {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content").then(({ data }) => setContent(data)); }, []);
  if (!content) return <p className="text-muted-c">Loading…</p>;

  const upd = (path, value) => {
    setContent((c) => {
      const n = JSON.parse(JSON.stringify(c));
      let ref = n; const keys = path.split(".");
      for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]];
      ref[keys[keys.length - 1]] = value;
      return n;
    });
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/content", content); await refresh(); toast.success("Content saved."); }
    catch { toast.error("Save failed."); }
    setSaving(false);
  };

  return (
    <div data-testid="content-panel">
      <Header title="Website Content" subtitle="Edit key text shown across the site." />
      <div className="space-y-5 max-w-2xl">
        <div className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-display font-bold text-t">Hero</h3>
          <div><label className={labelCls}>Headline</label><input className={inputCls} value={content.hero.headline} onChange={(e) => upd("hero.headline", e.target.value)} data-testid="content-hero-headline" /></div>
          <div><label className={labelCls}>Subheadline</label><input className={inputCls} value={content.hero.subheadline} onChange={(e) => upd("hero.subheadline", e.target.value)} /></div>
        </div>
        <div className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-display font-bold text-t">Reimagined for the Future</h3>
          <div><label className={labelCls}>Headline</label><input className={inputCls} value={content.bits2.headline} onChange={(e) => upd("bits2.headline", e.target.value)} /></div>
          <div><label className={labelCls}>Quote</label><textarea className={inputCls} rows={2} value={content.bits2.quote} onChange={(e) => upd("bits2.quote", e.target.value)} /></div>
        </div>
        <div className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-display font-bold text-t">About Story</h3>
          <textarea className={inputCls} rows={7} value={content.about.story} onChange={(e) => upd("about.story", e.target.value)} data-testid="content-about-story" />
        </div>
        <div className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-display font-bold text-t">Contact</h3>
          <div><label className={labelCls}>Business Hours</label><input className={inputCls} value={content.businessHours} onChange={(e) => upd("businessHours", e.target.value)} /></div>
          <div><label className={labelCls}>Roha Branch Phone</label><input className={inputCls} value={content.branches?.[0]?.phone || ""} onChange={(e) => upd("branches.0.phone", e.target.value)} /></div>
          <div><label className={labelCls}>Roha Branch Address</label><input className={inputCls} value={content.branches?.[0]?.address || ""} onChange={(e) => upd("branches.0.address", e.target.value)} /></div>
        </div>
        <div className="glass rounded-2xl p-6 space-y-4">
          <h3 className="font-display font-bold text-t">Counters (auto-calculated yearly)</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Established Year</label><input className={inputCls} value={content.config?.establishedYear ?? 1999} onChange={(e) => upd("config.establishedYear", Number(e.target.value))} /></div>
            <div><label className={labelCls}>ISO / Quality Label</label><input className={inputCls} value={content.config?.iso ?? ""} onChange={(e) => upd("config.iso", e.target.value)} /></div>
            <div><label className={labelCls}>Learner Baseline</label><input className={inputCls} value={content.config?.learnerBaseline ?? 18000} onChange={(e) => upd("config.learnerBaseline", Number(e.target.value))} /></div>
            <div><label className={labelCls}>Learners Added / Year</label><input className={inputCls} value={content.config?.learnerPerYear ?? 500} onChange={(e) => upd("config.learnerPerYear", Number(e.target.value))} /></div>
          </div>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary px-7 py-3.5 inline-flex items-center gap-2" data-testid="save-content"><Save size={17} /> {saving ? "Saving…" : "Save Content"}</button>
      </div>
    </div>
  );
}

/* ---------------- Generic CRUD ---------------- */
function emptyItem(fields) {
  const o = {};
  fields.forEach((f) => { o[f.key] = f.type === "checkbox" ? (f.key === "active") : ""; });
  return o;
}

function CrudPanel({ title, endpoint, fields, refresh, extraDefaults = {} }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // item being edited or new
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/${endpoint}`); setItems(data); } catch { toast.error("Load failed"); }
    setLoading(false);
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = { ...extraDefaults, ...editing };
    try {
      if (editing.id) await api.put(`/${endpoint}/${editing.id}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success("Saved");
      setEditing(null);
      await load(); await refresh();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    await api.delete(`/${endpoint}/${id}`);
    await load(); await refresh();
    toast.success("Deleted");
  };

  const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  return (
    <div data-testid={`${endpoint}-panel`}>
      <div className="flex items-center justify-between mb-7">
        <Header title={title} subtitle={`${items.length} item(s).`} />
        <button onClick={() => setEditing(emptyItem(fields))} className="btn-primary px-5 py-2.5 inline-flex items-center gap-2 shrink-0" data-testid={`add-${endpoint}`}><Plus size={16} /> Add</button>
      </div>

      {editing && (
        <div className="glass-strong rounded-2xl p-6 mb-6 space-y-4" data-testid={`${endpoint}-form`}>
          {fields.map((f) => (
            <div key={f.key}>
              <label className={labelCls}>{f.label}</label>
              {f.type === "textarea" ? (
                <textarea className={inputCls} rows={3} value={editing[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} />
              ) : f.type === "select" ? (
                <select className={inputCls} value={editing[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)}>
                  <option value="">Select…</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-2 text-sm text-t"><input type="checkbox" checked={!!editing[f.key]} onChange={(e) => setField(f.key, e.target.checked)} /> {f.label}</label>
              ) : f.type === "image" ? (
                <div className="space-y-2">
                  <input className={inputCls} placeholder="Paste image URL" value={editing[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} />
                  <input type="file" accept="image/*" onChange={(e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    const r = new FileReader(); r.onload = () => setField(f.key, r.result); r.readAsDataURL(file);
                  }} className="text-xs text-muted-c" />
                  {editing[f.key] && <img src={editing[f.key]} alt="" className="w-16 h-16 rounded-full object-cover" />}
                </div>
              ) : (
                <input className={inputCls} type={f.type === "date" ? "date" : "text"} value={editing[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} />
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <button onClick={save} className="btn-primary px-6 py-2.5 inline-flex items-center gap-2" data-testid={`save-${endpoint}`}><Save size={16} /> Save</button>
            <button onClick={() => setEditing(null)} className="btn-ghost px-6 py-2.5">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <p className="text-muted-c">Loading…</p> : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="glass rounded-2xl p-5 flex items-start gap-4" data-testid={`${endpoint}-item-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-t">{it[fields[0].key]}</div>
                <div className="text-sm text-muted-c mt-1 line-clamp-2">{fields.slice(1).map((f) => f.type === "image" ? null : `${it[f.key]}`).filter(Boolean).join(" · ")}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditing(it)} className="btn-ghost px-4 py-2 text-sm">Edit</button>
                <button onClick={() => remove(it.id)} className="p-2.5 rounded-lg btn-ghost text-red-400"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Courses (special: category select) ---------------- */
function CoursesPanel({ refresh }) {
  const { data } = useSite();
  const categories = data?.categories || [];
  return (
    <CrudPanel title="Courses" endpoint="courses" refresh={refresh}
      fields={[
        { key: "name", label: "Course Name" },
        { key: "category", label: "Category", type: "select", options: categories.map((c) => c.id) },
        { key: "duration", label: "Duration" },
        { key: "level", label: "Level" },
        { key: "icon", label: "Icon name (lucide)" },
        { key: "description", label: "Description", type: "textarea" },
      ]} />
  );
}
